<template>
  <field-block :title="title">
    <p v-if="helpText" class="mb-1">{{ helpText }}</p>
    <v-autocomplete
      v-model="conId"
      :label="label"
      placeholder="Search contacts"
      item-text="label"
      item-value="value"
      :hint="hint"
      :persistent-hint="!!hint"
      :loading="loading"
      :items="contactsOptions"
      :search-input.sync="search"
      :disabled="disabled"
      :menu-props="menuProps"
    >
      <template v-slot:item="{ item, attrs, on }">
        <v-list-item
          v-bind="attrs"
          v-on="on"
          :class="{ 'opened-contact': isContactOpened(item.value) }"
        >
          <v-list-item-content>
            <v-list-item-title>{{ item.label }}</v-list-item-title>
          </v-list-item-content>
          <v-list-item-action class="mt-1 mb-1">
            <v-btn
              icon
              small
              @mousedown.stop
              @click.stop="openContactInNewTab(item.value)"
              :color="isContactOpened(item.value) ? 'success' : 'appMain'"
            >
              <v-icon small>open_in_new</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </template>

      <template v-slot:no-data>
        <contact-not-found
          v-if="showNotFoundBlock && !hideAddButton"
          class="pa-4"
          :showAddButton="canAddContact && !hideAddButton"
          :search="search"
          @created="selectCreatedContact"
        />
        <div v-else></div>
      </template>
    </v-autocomplete>
  </field-block>
</template>

<script>
import { mapActions } from 'vuex';
import ContactNotFound from '@/views/contacts/components/ContactNotFound';
import { PERMISSIONS } from '@/access/permissions';

export default {
  name: 'SelectContactField',
  components: {
    ContactNotFound,
  },
  props: {
    value: String,
    helpText: String,
    title: {
      default: 'Choose Contact',
      type: String,
    },
    label: String,
    hideAddButton: Boolean,
    disabled: Boolean,
    hint: String,
    excludeIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      contacts: [],
      searchDelay: null,
      loading: false,
      search: '',
      selectedContact: null,
      autocompleteWidth: null,
      lastOpenedContactId: null,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.autocompleteWidth = this.$el?.offsetWidth;
    });
  },

  computed: {
    menuProps() {
      return this.autocompleteWidth ? { maxWidth: this.autocompleteWidth } : {};
    },

    conId: {
      get() {
        return this.value;
      },
      set(conId) {
        this.$emit('input', conId);
        this.lastOpenedContactId = null;

        if (conId) {
          this.selectedContact = this.contacts.find((i) => i.con_id === conId);
        } else {
          this.selectedContact = null;
        }
      },
    },

    canAddContact() {
      return this.hasPermission(PERMISSIONS.ADD_CONTACTS);
    },

    showNotFoundBlock() {
      return (
        !!(this.search || '').trim() && !this.loading && !this.contacts.length
      );
    },

    contactsOptions() {
      return [...this.contacts, this.selectedContact].reduce((acc, contact) => {
        if (contact?.con_id && !this.excludeIds.includes(contact.con_id)) {
          acc.push({
            label: this.getContactName(contact),
            value: contact.con_id,
          });
        }

        return acc;
      }, []);
    },
  },
  watch: {
    search(val) {
      this.searchContacts(val);
    },
    value(conId) {
      this.loadSelectedContact(conId);
    },
  },
  methods: {
    ...mapActions(['loadFilteredContacts', 'loadSingleContactShort']),

    searchContacts(search) {
      clearTimeout(this.searchDelay);

      this.searchDelay = setTimeout(() => {
        this.search = search || '';

        if (!search) {
          return;
        }

        this.loading = true;
        this.loadFilteredContacts({
          filters: {
            fields: ['first_name', 'last_name', 'name'],
            limit: 10,
            name: search,
          },
        })
          .then(({ results }) => {
            this.contacts = results || [];
          })
          .finally(() => {
            this.loading = false;
          });
      }, 200);
    },

    selectCreatedContact({ con_id }) {
      this.conId = con_id;
    },

    isContactOpened(conId) {
      return this.lastOpenedContactId === conId;
    },

    openContactInNewTab(conId) {
      if (!conId) return;

      this.lastOpenedContactId = conId;

      const route = this.$router.resolve({
        name: 'contact-edit',
        params: { id: conId },
      });

      window.open(route.href, '_blank');
    },

    loadSelectedContact(conId) {
      if (!conId) {
        this.selectedContact = null;
        return;
      }

      if (this.selectedContact?.con_id === conId) {
        return;
      }

      this.loadSingleContactShort({
        filters: { id: conId },
      }).then((contact) => {
        if (contact) {
          this.selectedContact = contact;
        }
      });
    },
  },
};
</script>

<style>
.opened-contact {
  background: var(--v-lightGray-base) !important;

  .v-list-item__title {
    color: var(--v-success-base);
  }
}
</style>
